// Scarf + coat-tail cloth (ART_DIRECTION §7 "Scarf as fabric"): ribbons of PBD/verlet particles,
// top row pinned to a bone, gravity + drag toward the air (world wind) so the cloth streams back
// when the wearer runs or flies, sphere collision with chest / pack / hips. Variable-dt safe:
// the frame is split into <= 1/60 s substeps with the pinned row interpolated across them, so it
// is smooth at 30, 60 or 144 Hz. Every ribbon of every cowboy writes into ONE dynamic geometry
// (one draw call). Cosmetic and local: never networked.

import * as THREE from 'three';
import { withHaze } from '../materials';
export type V3 = [number, number, number];
export interface RibbonDef {
  bone: number;
  /** model-space anchor of each column of the pinned top row */
  anchors: V3[];
  rows: number;
  seg: number;
  /** extra length on the last link per column (tattered hem) */
  hem: number[];
  color: number;
  /** <1 = heavy cloth that answers the wind slowly */
  windK: number;
  /** initial hang direction in model space (usually down/back) */
  hang: V3;
  /** how much narrower the last row is than the knot (0..1, default 0.25) */
  taper?: number;
  /** the first `n` links of the rest drape run along `dir` (model space) before turning down the hang line: tails
   *  knotted at the neck lie back over the top of the pack, then fall down its outer face */
  bend?: { n: number; dir: V3 };
  /** the rest drape rides this bone instead of `bone` (same bind): tails knotted at the neck but lying on the pack
   *  keep to the pack when the chest twists */
  restBone?: number;
}

const GRAV = -9.8;
/** rest-shape spring (1/s^2): ~1 Hz sway, gravity and the air still bend it */
const SHAPE_K = 45;
export const WORLD_WIND = new THREE.Vector3(1.4, 0.15, 0.7);
const MAX_VERTS = 512;
const MAX_INDEX = MAX_VERTS * 6;

const _v = new THREE.Vector3();

export class Ribbon {
  readonly cols: number;
  readonly rows: number;
  readonly n: number;
  readonly pos: Float32Array;
  readonly old: Float32Array;
  readonly vel: Float32Array;
  readonly restV: Float32Array; // per vertical link (row r -> r+1) per column
  readonly restH: Float32Array; // per row, per gap
  readonly local: THREE.Vector3[]; // anchor in bone space
  readonly a0: Float32Array; // pinned row at the start of the frame (world)
  readonly a1: Float32Array; // pinned row at the end of the frame (world)
  readonly hang: THREE.Vector3;
  /** rest drape (bone space, per particle): straight down the hang line from each knot column */
  readonly restLocal: Float32Array;
  /** the anchor bone's world matrix this frame (elements), for the rest-shape pull */
  private readonly bm = new Float32Array(16);
  base = 0; // first vertex in the batch
  live = false;
  dirtyColor = true;
  /** joint = the anchor bone's bind position (model space); invQ = inverse of its bind rotation */
  constructor(public def: RibbonDef, joint: V3, invQ?: THREE.Quaternion) {
    this.cols = def.anchors.length;
    this.rows = def.rows;
    this.n = this.cols * this.rows;
    this.pos = new Float32Array(this.n * 3);
    this.old = new Float32Array(this.n * 3);
    this.vel = new Float32Array(this.n * 3);
    this.restV = new Float32Array((this.rows - 1) * this.cols);
    for (let r = 0; r < this.rows - 1; r++)
      for (let c = 0; c < this.cols; c++) this.restV[r * this.cols + c] = def.seg + (r === this.rows - 2 ? def.hem[c] ?? 0 : 0);
    this.restH = new Float32Array(Math.max(1, this.cols - 1) * this.rows);
    const j = joint;
    this.local = def.anchors.map((a) => { const v = new THREE.Vector3(a[0] - j[0], a[1] - j[1], a[2] - j[2]); return invQ ? v.applyQuaternion(invQ) : v; });
    for (let r = 0; r < this.rows; r++) {
      // cloth narrows a touch toward the tail
      const taper = 1 - (def.taper ?? 0.25) * (r / Math.max(1, this.rows - 1));
      for (let c = 0; c < this.cols - 1; c++) {
        const a = def.anchors[c], b = def.anchors[c + 1];
        this.restH[r * (this.cols - 1) + c] = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) * taper;
      }
    }
    this.a0 = new Float32Array(this.cols * 3);
    this.a1 = new Float32Array(this.cols * 3);
    this.hang = new THREE.Vector3(def.hang[0], def.hang[1], def.hang[2]).normalize();
    if (invQ) this.hang.applyQuaternion(invQ);
    this.restLocal = new Float32Array(this.n * 3);
    const bendN = def.bend?.n ?? 0;
    const bendD = def.bend ? new THREE.Vector3(...def.bend.dir).normalize() : null;
    if (bendD && invQ) bendD.applyQuaternion(invQ);
    for (let r = 0; r < this.rows; r++)
      for (let c = 0; c < this.cols; c++) {
        const i = (r * this.cols + c) * 3, tp = 1 - (def.taper ?? 0.25) * (r / Math.max(1, this.rows - 1));
        // columns gather toward the tail's centre line as it tapers
        const mid = this.local[(this.cols - 1) >> 1];
        const lx = mid.x + (this.local[c].x - mid.x) * tp, ly = mid.y + (this.local[c].y - mid.y) * tp, lz = mid.z + (this.local[c].z - mid.z) * tp;
        const b = bendD ? Math.min(r, bendN) * def.seg : 0, d = (r - Math.min(r, bendN)) * def.seg;
        this.restLocal[i] = lx + this.hang.x * d + (bendD ? bendD.x * b : 0);
        this.restLocal[i + 1] = ly + this.hang.y * d + (bendD ? bendD.y * b : 0);
        this.restLocal[i + 2] = lz + this.hang.z * d + (bendD ? bendD.z * b : 0);
      }
  }

  /** write the pinned row for this frame from the bone's world matrix */
  setAnchors(m: THREE.Matrix4) {
    this.bm.set(m.elements);
    this.a0.set(this.a1);
    for (let c = 0; c < this.cols; c++) {
      _v.copy(this.local[c]).applyMatrix4(m);
      this.a1[c * 3] = _v.x; this.a1[c * 3 + 1] = _v.y; this.a1[c * 3 + 2] = _v.z;
    }
  }

  /** the rest drape's frame, when it rides another bone than the anchors (RibbonDef.restBone); after setAnchors */
  setRestFrame(m: THREE.Matrix4) { this.bm.set(m.elements); }

  /** drop the cloth straight down from the anchors (spawn / teleport) */
  reset(m: THREE.Matrix4, hangWorld: THREE.Vector3, restM?: THREE.Matrix4) {
    this.setAnchors(m);
    if (restM) this.setRestFrame(restM);
    this.a0.set(this.a1);
    const e = (restM ?? m).elements;
    for (let r = 0; r < this.rows; r++)
      for (let c = 0; c < this.cols; c++) {
        const i = (r * this.cols + c) * 3;
        if (this.def.bend) {
          // spawn on the rest drape (over the pack), not straight down through it
          const lx = this.restLocal[i], ly = this.restLocal[i + 1], lz = this.restLocal[i + 2];
          this.pos[i] = e[0] * lx + e[4] * ly + e[8] * lz + e[12];
          this.pos[i + 1] = e[1] * lx + e[5] * ly + e[9] * lz + e[13];
          this.pos[i + 2] = e[2] * lx + e[6] * ly + e[10] * lz + e[14];
          if (r === 0) { this.pos[i] = this.a1[c * 3]; this.pos[i + 1] = this.a1[c * 3 + 1]; this.pos[i + 2] = this.a1[c * 3 + 2]; }
        } else {
          const d = r * this.def.seg;
          this.pos[i] = this.a1[c * 3] + hangWorld.x * d;
          this.pos[i + 1] = this.a1[c * 3 + 1] + hangWorld.y * d;
          this.pos[i + 2] = this.a1[c * 3 + 2] + hangWorld.z * d;
        }
        this.vel[i] = this.vel[i + 1] = this.vel[i + 2] = 0;
      }
    this.live = true;
  }

  /**
   * Advance by dt. airX/Y/Z is the world air velocity (wind; the wearer's own velocity is
   * already in the particles, so drag toward the air streams the cloth back when moving).
   */
  step(dt: number, iters: number, maxH: number, airX: number, airY: number, airZ: number, time: number, spheres: Float32Array, nSph: number, box?: Float32Array | null) {
    const steps = Math.max(1, Math.ceil(dt / maxH - 1e-4));
    const h = dt / steps;
    const { cols, rows, pos, old, vel } = this;
    const drag = 1.3 * this.def.windK; // gravity must win at a run: a scarf droops and swings, it does not stream like a flag
    const gust = 0.75 + 0.25 * Math.sin(time * 1.3) + 0.15 * Math.sin(time * 3.7);
    for (let s = 1; s <= steps; s++) {
      const al = s / steps;
      // pinned row, interpolated across the frame
      for (let c = 0; c < cols; c++) {
        const i = c * 3;
        pos[i] = this.a0[i] + (this.a1[i] - this.a0[i]) * al;
        pos[i + 1] = this.a0[i + 1] + (this.a1[i + 1] - this.a0[i + 1]) * al;
        pos[i + 2] = this.a0[i + 2] + (this.a1[i + 2] - this.a0[i + 2]) * al;
      }
      // integrate free particles
      for (let p = cols; p < this.n; p++) {
        const i = p * 3;
        const r = (p / cols) | 0;
        // flutter: a travelling wave down the ribbon, stronger the faster the air
        const rel = Math.abs(airX - vel[i]) + Math.abs(airZ - vel[i + 2]) + Math.abs(airY - vel[i + 1]);
        const fl = Math.min(rel, 30) * 0.22 * Math.sin(time * 13 + r * 0.9 + p * 0.37);
        const ax = (airX * gust - vel[i]) * drag + fl * 0.4;
        const ay = (airY - vel[i + 1]) * drag + GRAV + fl;
        const az = (airZ * gust - vel[i + 2]) * drag - fl * 0.3;
        // rest-shape pull: every particle is drawn toward its place on the rest drape (bone space -> world), so at
        // idle the tail HANGS down the pack and only sways when the body moves; it can never stand up or flag out
        {
          const e = this.bm, lx = this.restLocal[i], ly = this.restLocal[i + 1], lz = this.restLocal[i + 2];
          const tx = e[0] * lx + e[4] * ly + e[8] * lz + e[12], ty = e[1] * lx + e[5] * ly + e[9] * lz + e[13], tz = e[2] * lx + e[6] * ly + e[10] * lz + e[14];
          const ks = SHAPE_K * (1.15 - 0.5 * r / rows);
          vel[i] += (tx - pos[i]) * ks * h; vel[i + 1] += (ty - pos[i + 1]) * ks * h; vel[i + 2] += (tz - pos[i + 2]) * ks * h;
        }
        vel[i] += ax * h; vel[i + 1] += ay * h; vel[i + 2] += az * h;
        old[i] = pos[i]; old[i + 1] = pos[i + 1]; old[i + 2] = pos[i + 2];
        pos[i] += vel[i] * h; pos[i + 1] += vel[i + 1] * h; pos[i + 2] += vel[i + 2] * h;
      }
      // constraints
      for (let it = 0; it < iters; it++) {
        for (let r = 0; r < rows - 1; r++)
          for (let c = 0; c < cols; c++) this.link(r * cols + c, (r + 1) * cols + c, this.restV[r * cols + c], r === 0 ? 0 : 0.5);
        if (cols > 1)
          for (let r = 1; r < rows; r++)
            for (let c = 0; c < cols - 1; c++) this.link(r * cols + c, r * cols + c + 1, this.restH[r * (cols - 1) + c], 0.5);
        // collide
        for (let p = cols; p < this.n; p++) {
          const i = p * 3;
          for (let k = 0; k < nSph; k++) {
            const o = k * 4;
            const dx = pos[i] - spheres[o], dy = pos[i + 1] - spheres[o + 1], dz = pos[i + 2] - spheres[o + 2];
            const rr = spheres[o + 3];
            const d2 = dx * dx + dy * dy + dz * dz;
            if (d2 < rr * rr && d2 > 1e-8) {
              const d = Math.sqrt(d2), f = rr / d;
              pos[i] = spheres[o] + dx * f; pos[i + 1] = spheres[o + 1] + dy * f; pos[i + 2] = spheres[o + 2] + dz * f;
            }
          }
          if (box) boxOut(pos, i, box);
        }
      }
      // strain limit, top-down: cloth never stretches past 108 % however hard the air pulls
      for (let r = 0; r < rows - 1; r++)
        for (let c = 0; c < cols; c++) {
          const ia = (r * cols + c) * 3, ib = ((r + 1) * cols + c) * 3;
          const dx = pos[ib] - pos[ia], dy = pos[ib + 1] - pos[ia + 1], dz = pos[ib + 2] - pos[ia + 2];
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz), mx = this.restV[r * cols + c] * 1.08;
          if (d > mx) { const f = mx / d; pos[ib] = pos[ia] + dx * f; pos[ib + 1] = pos[ia + 1] + dy * f; pos[ib + 2] = pos[ia + 2] + dz * f; }
        }
      // a scarf never climbs above its knot: whatever the air, a collider or a tumble does, every free particle
      // stays below the lowest pinned point (+3 cm), so tails can't stand up or float above the hat
      {
        let ay = pos[1];
        for (let c = 1; c < cols; c++) ay = Math.min(ay, pos[c * 3 + 1]);
        const top = ay + 0.03;
        for (let p = cols; p < this.n; p++) if (pos[p * 3 + 1] > top) pos[p * 3 + 1] = top;
      }
      // velocities from the corrected positions (PBD), lightly damped
      const inv = 0.982 / h; // damped: the sway settles within ~a second
      for (let p = cols; p < this.n; p++) {
        const i = p * 3;
        vel[i] = (pos[i] - old[i]) * inv; vel[i + 1] = (pos[i + 1] - old[i + 1]) * inv; vel[i + 2] = (pos[i + 2] - old[i + 2]) * inv;
      }
    }
  }

  /** distance constraint; wA = share of the correction taken by a (0 when a is pinned) */
  private link(a: number, b: number, rest: number, wA: number) {
    const pos = this.pos;
    const ia = a * 3, ib = b * 3;
    const pinnedA = a < this.cols;
    const dx = pos[ib] - pos[ia], dy = pos[ib + 1] - pos[ia + 1], dz = pos[ib + 2] - pos[ia + 2];
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d < 1e-6) return;
    const diff = (d - rest) / d;
    const ka = pinnedA ? 0 : wA, kb = pinnedA ? 1 : 1 - wA;
    pos[ia] += dx * diff * ka; pos[ia + 1] += dy * diff * ka; pos[ia + 2] += dz * diff * ka;
    pos[ib] -= dx * diff * kb; pos[ib + 1] -= dy * diff * kb; pos[ib + 2] -= dz * diff * kb;
  }
}

/** All ribbons, one dynamic geometry, one draw. */
export class ClothBatch {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.BufferGeometry;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly colAttr: THREE.BufferAttribute;
  private readonly idxAttr: THREE.BufferAttribute;
  private readonly uvAttr: THREE.BufferAttribute;
  private ribbons: Ribbon[] = [];
  private readonly tmpCol = new THREE.Color();
  private readonly tmpCol2 = new THREE.Color();

  constructor() {
    this.geo = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(new Float32Array(MAX_VERTS * 3), 3);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr = new THREE.BufferAttribute(new Float32Array(MAX_VERTS * 3), 3);
    this.colAttr.setUsage(THREE.DynamicDrawUsage);
    const nrm = new THREE.BufferAttribute(new Float32Array(MAX_VERTS * 3), 3);
    nrm.setUsage(THREE.DynamicDrawUsage);
    this.idxAttr = new THREE.BufferAttribute(new Uint16Array(MAX_INDEX), 1);
    this.geo.setAttribute('position', this.posAttr);
    this.geo.setAttribute('normal', nrm);
    this.geo.setAttribute('color', this.colAttr);
    this.uvAttr = new THREE.BufferAttribute(new Float32Array(MAX_VERTS * 2), 2);
    this.geo.setAttribute('clothUv', this.uvAttr);
    this.geo.setIndex(this.idxAttr);
    this.geo.setDrawRange(0, 0);
    this.mesh = new THREE.Mesh(this.geo, fabricMaterial());
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = false;
    this.mesh.name = 'cowboy-cloth';
  }

  /** (re)assign the batch layout; only on spawn / class change */
  setRibbons(list: Ribbon[]) {
    this.ribbons = list;
    let v = 0, ii = 0;
    const idx = this.idxAttr.array as Uint16Array;
    for (const r of list) {
      if (v + r.n > MAX_VERTS) { r.base = -1; continue; }
      r.base = v;
      r.dirtyColor = true;
      const uv = this.uvAttr.array as Float32Array;
      const seed = list.indexOf(r) * 2;
      for (let y = 0; y < r.rows; y++)
        for (let x = 0; x < r.cols; x++) {
          const k = (v + y * r.cols + x) * 2;
          uv[k] = seed + 0.01 + 0.98 * x / Math.max(1, r.cols - 1);
          uv[k + 1] = y / Math.max(1, r.rows - 1);
        }
      this.uvAttr.needsUpdate = true;
      for (let y = 0; y < r.rows - 1; y++)
        for (let x = 0; x < r.cols - 1; x++) {
          const a = v + y * r.cols + x, b = a + r.cols;
          idx[ii++] = a; idx[ii++] = b; idx[ii++] = a + 1;
          idx[ii++] = a + 1; idx[ii++] = b; idx[ii++] = b + 1;
        }
      v += r.n;
    }
    idx.fill(0, ii);
    this.idxAttr.needsUpdate = true;
    this.geo.setDrawRange(0, ii);
  }

  /** copy a ribbon's particles in; tint = cloak shimmer amount */
  write(r: Ribbon, cloak: number, shimmer: number, visible: boolean) {
    if (r.base < 0) return;
    const out = this.posAttr.array as Float32Array;
    const o = r.base * 3;
    if (!visible) {
      // collapse to the anchor: degenerate, costs nothing
      for (let i = 0; i < r.n * 3; i += 3) { out[o + i] = r.a1[0]; out[o + i + 1] = r.a1[1] - 1000; out[o + i + 2] = r.a1[2]; }
      return;
    }
    out.set(r.pos, o);
    if (r.dirtyColor || cloak > 0) {
      const col = this.colAttr.array as Float32Array;
      const base = this.tmpCol.setHex(r.def.color);
      const shim = this.tmpCol2.setHex(0x8fc7c3);
      for (let y = 0; y < r.rows; y++)
        for (let x = 0; x < r.cols; x++) {
          const i = o + (y * r.cols + x) * 3;
          // printed stripe near the hem + darker frayed edge
          let k = 1;
          if (x === 0 || x === r.cols - 1) k *= 0.93;
          let cr = base.r * k, cg = base.g * k, cb = base.b * k;
          if (cloak > 0) {
            const s = cloak * (0.6 + 0.4 * Math.sin(shimmer * 9 + y * 0.8));
            cr += (shim.r - cr) * s; cg += (shim.g - cg) * s; cb += (shim.b - cb) * s;
          }
          col[i] = cr; col[i + 1] = cg; col[i + 2] = cb;
        }
      this.colAttr.needsUpdate = true;
      r.dirtyColor = cloak > 0;
    }
  }

  commit() {
    this.posAttr.needsUpdate = true;
    // allocation-free normals over the live index range
    const nAttr = this.geo.attributes.normal as THREE.BufferAttribute;
    const nr = nAttr.array as Float32Array, ps = this.posAttr.array as Float32Array;
    const idx = this.idxAttr.array as Uint16Array;
    const cnt = this.geo.drawRange.count;
    nr.fill(0);
    for (let t = 0; t < cnt; t += 3) {
      const a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
      const e1x = ps[b] - ps[a], e1y = ps[b + 1] - ps[a + 1], e1z = ps[b + 2] - ps[a + 2];
      const e2x = ps[c] - ps[a], e2y = ps[c + 1] - ps[a + 1], e2z = ps[c + 2] - ps[a + 2];
      const nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
      nr[a] += nx; nr[a + 1] += ny; nr[a + 2] += nz;
      nr[b] += nx; nr[b + 1] += ny; nr[b + 2] += nz;
      nr[c] += nx; nr[c + 1] += ny; nr[c + 2] += nz;
    }
    for (let i = 0; i < nr.length; i += 3) {
      const l = Math.hypot(nr[i], nr[i + 1], nr[i + 2]);
      if (l > 1e-12) { nr[i] /= l; nr[i + 1] /= l; nr[i + 2] /= l; } else nr[i + 1] = 1;
    }
    nAttr.needsUpdate = true;
  }

  dispose() {
    this.geo.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}


/**
 * Woven cloth: the slot colour (vertex colour) broken up by a fine weave and soft folds, a darker
 * printed band near the hem, a frayed, alpha-cut tattered edge, soft-lit to match the characters.
 */
function fabricMaterial() {
  const vals = [70, 105, 165, 220, 245, 255];
  const data = new Uint8Array(vals.length * 4);
  vals.forEach((v, i) => data.set([v, v, v, 255], i * 4));
  const ramp = new THREE.DataTexture(data, vals.length, 1, THREE.RGBAFormat);
  ramp.minFilter = ramp.magFilter = THREE.LinearFilter; ramp.generateMipmaps = false; ramp.needsUpdate = true;
  const m = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: ramp, side: THREE.DoubleSide, alphaTest: 0.5 });
  withHaze(m, 0.32);
  const prev = m.onBeforeCompile;
  m.onBeforeCompile = (shader, r) => {
    prev.call(m, shader, r);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec2 clothUv;\nvarying vec2 vCl;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvCl = clothUv;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec2 vCl;
        float h21( vec2 p ) { return fract( sin( dot( p, vec2( 41.3, 289.1 ) ) ) * 45758.5453 ); }
        float vn( vec2 p ) { vec2 i = floor( p ), f = fract( p ); f = f * f * ( 3.0 - 2.0 * f );
          return mix( mix( h21( i ), h21( i + vec2( 1, 0 ) ), f.x ), mix( h21( i + vec2( 0, 1 ) ), h21( i + vec2( 1, 1 ) ), f.x ), f.y ); }`)
      .replace('#include <color_fragment>', `#include <color_fragment>
        {
          // same dyed-wool treatment as the scarf on the body: a touch dusty and deeper than the raw slot colour
          float dl = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );
          diffuseColor.rgb = mix( diffuseColor.rgb, vec3( dl ), 0.05 ) * 1.0;
          // tattered hem: ragged alpha cut in the last fifth
          float rag = vn( vec2( vCl.x * 9.0, 0.5 ) ) * 0.6 + vn( vec2( vCl.x * 23.0, 3.0 ) ) * 0.4;
          float cut = 0.8 + 0.2 * rag;
          if ( vCl.y > cut ) discard;
          // weave + long soft folds + wear
          float weave = 0.5 + 0.5 * sin( vCl.x * 140.0 ) * sin( vCl.y * 260.0 );
          float folds = vn( vec2( vCl.x * 3.0, vCl.y * 1.5 ) );
          float wear = vn( vec2( vCl.x * 14.0, vCl.y * 30.0 ) );
          // heavy wool: deep lengthwise folds (the tail gathers where it is knotted), a coarse weave and a darker
          // underside, so even a 30 px tail reads as cloth rather than a ribbon of paint
          float crease = 0.5 + 0.5 * sin( fract( vCl.x ) * 6.2832 * 2.0 + folds * 2.0 );
          // pleats: the tail's five columns alternate in/out from the knot, so shade the same rhythm as fold AO
          float pleat = 0.5 + 0.5 * cos( fract( vCl.x ) * 6.2832 * 2.0 );
          diffuseColor.rgb *= 0.7 + 0.08 * weave + 0.16 * folds + 0.1 * crease * smoothstep( 0.0, 0.3, vCl.y );
          diffuseColor.rgb *= 0.62 + 0.38 * pleat;
          if ( !gl_FrontFacing ) diffuseColor.rgb *= 0.72;
          diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * vec3( 0.75, 0.7, 0.62 ), smoothstep( 0.55, 0.9, wear ) * 0.6 );
          // printed stripe near the hem, darker frayed sides
          float band = smoothstep( 0.6, 0.62, vCl.y ) * ( 1.0 - smoothstep( 0.66, 0.68, vCl.y ) );
          diffuseColor.rgb *= 1.0 - 0.3 * band;
          float side = abs( fract( vCl.x ) - 0.5 ) * 2.0;
          diffuseColor.rgb *= 1.0 - 0.15 * smoothstep( 0.8, 1.0, side );
          // ink: the same dark contour the body's outline gives, along both selvedges and the frayed hem
          float ink = max( smoothstep( 0.86, 0.97, side ), smoothstep( cut - 0.035, cut - 0.005, vCl.y ) );
          diffuseColor.rgb *= 1.0 - 0.6 * ink;
        }`);
  };
  m.customProgramCacheKey = () => 'cowboyFabric';
  return m;
}

/**
 * A box collider (the jet pack): `box` = world->box matrix (16), box->world matrix (16), then min xyz, max xyz in
 * the box frame (the model's bind frame: back is -z, so +z is the face against the body). A particle inside leaves
 * through the nearest face, never the body side, so a tail can only end up on top of, behind or beside the pack.
 */
export function boxOut(pos: Float32Array, i: number, box: Float32Array) {
  const x = pos[i], y = pos[i + 1], z = pos[i + 2];
  const lx = box[0] * x + box[4] * y + box[8] * z + box[12];
  const ly = box[1] * x + box[5] * y + box[9] * z + box[13];
  const lz = box[2] * x + box[6] * y + box[10] * z + box[14];
  const x0 = box[32], y0 = box[33], z0 = box[34], x1 = box[35], y1 = box[36], z1 = box[37];
  if (lx <= x0 || lx >= x1 || ly <= y0 || ly >= y1 || lz <= z0 || lz >= z1) return;
  const m = 0.012;
  let best = y1 - ly, ax = 1; // top
  if (lz - z0 < best) { best = lz - z0; ax = 2; } // back face
  if (lx - x0 < best) { best = lx - x0; ax = 3; }
  if (x1 - lx < best) { best = x1 - lx; ax = 4; }
  if (ly - y0 < best) { best = ly - y0; ax = 5; }
  let nx = lx, ny = ly, nz = lz;
  if (ax === 1) ny = y1 + m; else if (ax === 2) nz = z0 - m; else if (ax === 3) nx = x0 - m; else if (ax === 4) nx = x1 + m; else ny = y0 - m;
  pos[i] = box[16] * nx + box[20] * ny + box[24] * nz + box[28];
  pos[i + 1] = box[17] * nx + box[21] * ny + box[25] * nz + box[29];
  pos[i + 2] = box[18] * nx + box[22] * ny + box[26] * nz + box[30];
}
